import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { isLogin } from '../Utils/Cookies';
import { stripParenthesizedText } from '../Utils/Strings';

// Set to true to show story audio controls again.
const SHOW_STORY_AUDIO = false

const ReaderDialog = ({ title, children, onClose, initialFocusRef }) => {
  const dialogRef = useRef(null)
  const closeRef = useRef(onClose)
  closeRef.current = onClose

  useEffect(() => {
    const previouslyFocused = document.activeElement
    const originalOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    const dialog = dialogRef.current
    ;(initialFocusRef?.current || dialog.querySelector('button'))?.focus()
    const handleKeyDown = event => {
      if (event.key === 'Escape') {
        event.preventDefault()
        event.stopPropagation()
        closeRef.current()
      }
      if (event.key !== 'Tab') return
      const controls = [...dialog.querySelectorAll('button:not(:disabled), textarea:not(:disabled), a[href]')]
      const first = controls[0]
      const last = controls[controls.length - 1]
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus() }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus() }
    }
    dialog.addEventListener('keydown', handleKeyDown)
    return () => {
      document.body.style.overflow = originalOverflow
      dialog.removeEventListener('keydown', handleKeyDown)
      if (previouslyFocused?.isConnected) previouslyFocused.focus()
    }
  }, [initialFocusRef])

  return <div className='reader-dialog-backdrop' onClick={onClose}>
    <section className='reader-dialog' role='dialog' aria-modal='true' aria-labelledby='reader-dialog-title' ref={dialogRef} onClick={event => event.stopPropagation()}>
      <header><h2 id='reader-dialog-title'>{title}</h2><button type='button' className='reader-dialog-close' onClick={onClose} aria-label='닫기'>×</button></header>
      {children}
    </section>
  </div>
}

export const StoryCover = ({ script, stories, onNext, isMyStory, instagram, queueInfo, isSampleStory }) => {
  if (!script || !stories.length) return null
  const imageUrl = stories[0].imageUrl || stories[0].defaultImageUrl
  const complete = Boolean(stories[0].imageUrl && isMyStory)
  let statusText = ''
  if (queueInfo?.holding) statusText = '동화 제작 과정에서 확인이 필요한 부분을 살펴보고 있어요.'
  else if (queueInfo?.pendingCount) statusText = `우리 아이의 동화를 만들고 있어요. 남은 작업: ${queueInfo.pendingCount > 20 ? '20개 이상' : `${queueInfo.pendingCount}개`}`
  else if (!isSampleStory && !complete) statusText = '우리 아이의 동화 제작을 준비하고 있어요.'

  return <main className='reader-cover'>
    <Link className='book-text-link reader-back' to='/fairytales'><span aria-hidden='true'>‹</span> 이야기 책장</Link>
    <article className='reader-cover-book'>
      <div className='reader-cover-image'>
        <img src={imageUrl} alt={`${script.title} 표지`} fetchpriority='high' />
        <span className='reader-cover-badge'>{isSampleStory ? '미리보기 동화' : '우리 아이의 동화'}</span>
      </div>
      <div className='reader-cover-copy'>
        <p className='book-eyebrow'><span aria-hidden='true'>✦</span> 함께 읽는 특별한 이야기</p>
        <h1 className='book-title'>{script.title}</h1>
        <p className='book-description'>{script.summary}</p>
        <p className='reader-cover-meta'>{stories.length}페이지의 모험{SHOW_STORY_AUDIO && stories.some(story => story.audioUrl) && <span> · 소리로도 들을 수 있어요</span>}</p>
      </div>
    </article>
    {statusText && <p className='reader-production-note' role='status'>{statusText}</p>}
    <button type='button' className='book-primary-button reader-start' onClick={onNext}>동화 읽기 <span aria-hidden='true'>→</span></button>
    <p className='reader-cover-note'>아이와 나란히 앉아, 첫 장을 펼쳐보세요.</p>
    {instagram && <a className='book-text-link reader-instagram' href={instagram} target='_blank' rel='noopener noreferrer'>인스타그램에서 보기 <span aria-hidden='true'>↗</span></a>}
  </main>
}

export const StoryPage = ({ story, onBefore, onNext, isLastPage, instagram, isMyStory, onSave, onImageRequest, onAudioToggle, isAudioPlaying, isAudioPaused, onStopAudio, scriptNo, isSampleStory, pageNumber, totalPages, title }) => {
  const navigate = useNavigate()
  const [isEditing, setIsEditing] = useState(false)
  const [editContent, setEditContent] = useState('')
  const [dialog, setDialog] = useState(null)
  const [imageRequest, setImageRequest] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [notice, setNotice] = useState('')
  const editRef = useRef(null)
  const requestRef = useRef(null)
  const editButtonRef = useRef(null)
  const canEdit = isMyStory && isLogin()
  const audioLabel = isAudioPlaying ? '듣기 멈추기' : isAudioPaused ? '이어서 듣기' : '동화 듣기'

  useEffect(() => { if (isEditing) editRef.current?.focus() }, [isEditing])

  const goToFairytaleList = () => {
    const listParams = new URLSearchParams({ beforeScriptNo: String(scriptNo) })
    if (isSampleStory) listParams.set('sample', 'true')
    onStopAudio()
    navigate(`/fairytales?${listParams.toString()}`)
  }

  const closeEditor = () => {
    setIsEditing(false)
    setError('')
    window.requestAnimationFrame(() => editButtonRef.current?.focus())
  }

  const handleSave = async () => {
    if (saving || !window.confirm('저장하면 이전 동화 내용으로 복구할 수 없습니다. 저장하시겠습니까?')) return
    setSaving(true)
    setError('')
    try {
      await onSave(story, editContent)
      closeEditor()
      setNotice('동화 내용을 저장했어요.')
    } catch (saveError) {
      setError('저장하지 못했어요. 잠시 후 다시 시도해 주세요.')
    } finally { setSaving(false) }
  }

  const handleImageRequest = async () => {
    if (saving || !imageRequest.trim()) return
    setSaving(true)
    setError('')
    try {
      await onImageRequest(story, imageRequest)
      setDialog(null)
      setNotice('수정 요청을 보냈어요. 내용을 확인하고 반영할게요.')
      window.requestAnimationFrame(() => editButtonRef.current?.focus())
    } catch (requestError) {
      setError('요청을 보내지 못했어요. 잠시 후 다시 시도해 주세요.')
    } finally { setSaving(false) }
  }

  return <main className='reader-content'>
    <header className='reader-page-heading'>
      <div><p className='book-eyebrow'>지금 함께 읽는 이야기</p><h1>{title}</h1></div>
      <span className='reader-page-count' aria-label={`전체 ${totalPages}페이지 중 ${pageNumber}페이지`}><strong>{pageNumber}</strong> / {totalPages}</span>
    </header>
    <progress className='reader-progress' max={totalPages} value={pageNumber} aria-label='동화 읽기 진행률' />
    <article className='reader-paper' aria-label={`${pageNumber}페이지`}>
      <img className='reader-illustration' src={story.imageUrl || story.defaultImageUrl} alt={`${title}, ${pageNumber}페이지 삽화`} />
      <div className='reader-page-body'>
        <div className='reader-page-tools'>
          {SHOW_STORY_AUDIO && story.audioUrl ? <button type='button' className={'reader-audio' + (isAudioPlaying ? ' is-playing' : '')} onClick={() => onAudioToggle(story)} aria-label={audioLabel} aria-pressed={isAudioPlaying} disabled={isEditing || saving}>
            <span className='reader-audio-symbol' aria-hidden='true'>{isAudioPlaying ? 'Ⅱ' : '▶'}</span>
            {audioLabel}
            {isAudioPlaying && <span className='reader-audio-levels' aria-hidden='true'><i className='audio-level-bar' /><i className='audio-level-bar' /><i className='audio-level-bar' /></span>}
          </button> : <span className='reader-reading-label'>함께 읽어요</span>}
          {canEdit && !isEditing && <button ref={editButtonRef} type='button' className='reader-edit-button' aria-label='동화 수정 메뉴' onClick={() => { onStopAudio(); setError(''); setNotice(''); setDialog('menu') }}><img src='/image/edit.svg' alt='' width='17' height='17' /><span>수정</span></button>}
        </div>

        {isEditing ? <div className='reader-editor'>
          <label htmlFor='reader-edit-content'>동화 내용 수정</label>
          <textarea id='reader-edit-content' ref={editRef} value={editContent} onChange={event => setEditContent(event.target.value)} rows={9} disabled={saving} />
          <p className='reader-editor-note'>저장하면 이전 내용으로 되돌릴 수 없어요.</p>
          {error && <p className='book-error' role='alert'>{error}</p>}
          <div className='reader-dialog-actions'><button type='button' className='book-secondary-button' onClick={closeEditor} disabled={saving}>취소</button><button type='button' className='book-primary-button' onClick={handleSave} disabled={saving || !editContent.trim()}>{saving ? '저장 중…' : '저장하기'}</button></div>
        </div> : <div className='reader-story-text'>{stripParenthesizedText(story.content)}</div>}

        {notice && <p className='reader-notice' role='status'>{notice}</p>}
        <div className='reader-page-end' aria-hidden='true'><span />✦<span /></div>
      </div>
    </article>
    {isLastPage && !isEditing && <section className='reader-finish'>
      <img src='/image/main/books.png' alt='' width='36' height='36' />
      <h2>한 편의 모험을 마쳤어요.</h2>
      <p>가장 기억에 남는 장면은 무엇인가요?</p>
      {instagram && <a className='book-text-link' href={instagram} target='_blank' rel='noopener noreferrer'>인스타그램에서 보기 <span aria-hidden='true'>↗</span></a>}
    </section>}
    <nav className='reader-navigation' aria-label='동화 페이지 이동'>
      <button type='button' className='reader-prev' onClick={onBefore} disabled={isEditing || saving} aria-label={pageNumber === 1 ? '표지로 돌아가기' : '이전 페이지'}><span aria-hidden='true'>‹</span>{pageNumber === 1 ? '표지' : '이전'}</button>
      <button type='button' className='reader-list' onClick={goToFairytaleList} disabled={isEditing || saving}>다른 이야기</button>
      <button type='button' className='reader-next' onClick={isLastPage ? goToFairytaleList : onNext} disabled={isEditing || saving}>{isLastPage ? '책장으로' : '다음'}<span aria-hidden='true'>›</span></button>
    </nav>

    {dialog === 'menu' && <ReaderDialog title='이야기를 다듬어요' onClose={() => setDialog(null)}>
      <p className='reader-dialog-description'>어떤 부분을 바꾸고 싶으신가요?</p>
      <button className='reader-option' type='button' onClick={() => { setImageRequest(''); setDialog('request') }}><strong>동화 수정 요청</strong><span>어색한 내용이나 그림을 알려주세요.</span></button>
      <button className='reader-option' type='button' onClick={() => { setEditContent(story.content); setIsEditing(true); setDialog(null) }}><strong>내용 직접 수정</strong><span>우리 가족에게 맞게 글을 다듬어요.</span></button>
    </ReaderDialog>}
    {dialog === 'request' && <ReaderDialog title='동화 수정 요청' initialFocusRef={requestRef} onClose={() => { if (!saving) { setDialog(null); setError(''); window.requestAnimationFrame(() => editButtonRef.current?.focus()) } }}>
      <label className='reader-request-label' htmlFor='reader-image-request'>바꾸고 싶은 내용을 알려주세요</label>
      <textarea id='reader-image-request' ref={requestRef} value={imageRequest} onChange={event => setImageRequest(event.target.value)} placeholder='어떤 부분이 어색한지 자세히 적어주세요.' rows={5} disabled={saving} />
      <p className='reader-dialog-description'>내용을 확인하고 최대한 빠르게 수정해 드릴게요.</p>
      {error && <p className='book-error' role='alert'>{error}</p>}
      <div className='reader-dialog-actions'><button className='book-secondary-button' type='button' onClick={() => { setDialog(null); setError('') }} disabled={saving}>취소</button><button className='book-primary-button' type='button' onClick={handleImageRequest} disabled={saving || !imageRequest.trim()}>{saving ? '보내는 중…' : '요청 보내기'}</button></div>
    </ReaderDialog>}
  </main>
}
